import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border border-transparent px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none",
  {
    variants: {
      size: {
        default: "",
        sm: "text-[10px] px-1.5 py-0",
        md: "text-sm px-3 py-1",
        status: "min-h-[28px] backdrop-blur-sm",
      },
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        neutral: "bg-muted text-muted-foreground [a&]:hover:bg-muted/80",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90",
        success:
          "bg-success-muted text-success border border-success/30 [a&]:hover:bg-success-muted",
        warning:
          "bg-warning-muted text-warning border border-warning/30 [a&]:hover:bg-warning-muted",
        error:
          "bg-error-muted text-destructive border border-destructive/30 [a&]:hover:bg-error-muted",
        info: "bg-info-muted text-info border border-info/30 [a&]:hover:bg-info-muted",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        "primary-muted": "bg-primary-muted text-primary",
        overlay: "bg-foreground/80 text-background shadow-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Badge({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
